import { CloseOutlined, EditAttributesOutlined } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useRef } from "react";
import {
  Datagrid,
  downloadCSV,
  List,
  NumberField,
  RaRecord,
  TextField,
  useGetOne,
  useRecordContext,
  useRefresh,
} from "react-admin";
import sendRequest from "../dataProvider/sendRequest";
import PartnersListActions from "../partners/PartnersListActions";
import PartnersListAside from "../partners/PartnersListAside";
import { hmLightTheme } from "../themes/hmTheme";
import CustomAmountField from "../unit/CustomAmountField";
import CustomButtonCancel from "../unit/CustomButtonCancel";
import CustomButtonSubmit from "../unit/CustomButtonSubmit";
import CustomEditIcon from "../unit/CustomEditIcon";
import CustomEditLinkProductIcon from "../unit/CustomEditLinkProductIcon";
import CustomEditRemoveIcon from "../unit/CustomEditRemoveIcon";
import CustomEditTermIcon from "../unit/CustomEditTermIcon";
import CustomLoading from "../unit/CustomLoading";
import CustomUniqueURLIcon from "../unit/CustomUniqueURLIcon";
import PostPagination from "../unit/PostPagination";
import useNotify from "../unit/useNotify";
import EmployerAdd from "./EmployerAdd";
import EmployerEdit from "./EmployerEdit";
import SwitchIcon from "../unit/svgIcon/SwitchIcon";
import SwitchOnIcon from "../unit/svgIcon/SwitchOnIcon";
import HmLoading from "../unit/HmLoading";
import NoResults from "../unit/NoResults";
import NoPermission from "../unit/NoPermission";
import getFiltersFromURL from "../unit/getFiltersFromURL";
import getEmployersListFilters from "./EmployersListFilters";
import CustomNoResults from "../unit/CustomNoResults";
const CustomStatusField = ({ ...props }) => {
  let record = useRecordContext();
  if (!record) return null;
  let status = "Incomplete";
  if (record.iComplete == 1) {
    if (record.sStatus == "1" || record.sStatus == "Yes") {
      status = "Active";
    } else if (record.sStatus == "0" || record.sStatus == "No") {
      status = "Inactive";
    }
  }
  return <Typography>{status}</Typography>;
};
const CustomEmployerNameField = ({ ...props }) => {
  let record = useRecordContext();
  if (!record) return null;
  return record?.sCompany;
};
const CustomProductNameField = ({ ...props }) => {
  let record = useRecordContext();
  if (!record) return null;
  let productNameArray = record?.sProductName;

  let productNums = 0;
  if (productNameArray) {
    Object.keys(productNameArray).map((key) => {
      productNums++;
    });
  }

  return <Typography>{productNums}</Typography>;
};
const CustomApplicationFeeField = ({ ...props }) => {
  let record = useRecordContext();
  if (!record) return null;
  let showApplicationFee = record?.iApplicationFee;
  let applicationFee = record?.application_fee;
  let defaultApplicationFee = record?.default_application_fee;
  let sUniqueApplicationFee = record?.sUniqueApplicationFee;

  if (
    applicationFee != "" &&
    applicationFee != "NULL" &&
    applicationFee != null
  ) {
    showApplicationFee = defaultApplicationFee;
  } else {
    if (
      defaultApplicationFee != "" &&
      defaultApplicationFee != "NULL" &&
      defaultApplicationFee != null
    ) {
      showApplicationFee = defaultApplicationFee;
    } else {
      showApplicationFee = defaultApplicationFee;
    }
  }

  record.iApplicationFee = showApplicationFee;

  if (record?.iApplicationFee > 0 || record?.sUniqueApplicationFee == "Yes") {
    return (
      <NumberField
        {...props}
        source={props.source || "iApplicationFee"}
        options={{
          style: "currency",
          currency: "USD",
          currencyDisplay: "narrowSymbol",
          minimumFractionDigits: 2,
        }}
        sx={{ fontFamily: hmLightTheme?.palette?.numberFontFamily }}
      />
    );
  } else {
    return "N/A";
  }
};

const LoanAmountField = ({ ...props }) => {
  const record = useRecordContext();
  if (record?.iLoanLimit > 0 || record?.sUniqueLoanMax == "Yes") {
    return (
      <CustomAmountField
        source="iLoanLimit"
        label="Max Loan Amount"
        cellClassName="numericText"
      />
    );
  } else {
    return "N/A";
  }
};
const EmployerTypeExporter = (records: any) => {
  let data = [];
  let title = {};
  data = records.map((record: any) => {
    return {
      id: record.id,
      sName: record.sCompany,
      sCity: record.sCity,
      sState: record.sState,
      sContactor: record.sConnector,
      productNum: record.productNum,
      sStatus: record.sStatus == 1 ? "Active" : "Inactive",
    };
  });
  title = {
    id: "Emp #",
    sName: "Organization",
    sCity: "City",
    sState: "State",
    sContactor: "Contact Name",
    productNum: "Products Linked",
    sStatus: "Status",
  };
  return {
    title: title,
    data: data,
  };
};

const CustomExporter = (records: any) => {
  const filterData = EmployerTypeExporter(records);

  let csvTitle = filterData.title;
  let data = filterData.data;
  const currentFilters = getFiltersFromURL();
  const filterString = Object.entries(currentFilters)
    .map(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        return `[${getFieldTitle(key)} ${getFieldValueType(
          key
        )} "${getFieldValue(key, value)}"]`;
      }

      return "";
    })
    .filter(Boolean)
    .join("");
  const csvData = [{ ...csvTitle }, ...data];
  const csvDataStr = csvData.map((row) => Object.values(row).join(","));

  if (filterString) {
    csvDataStr.push("");
    csvDataStr.push("Filters Applied:" + filterString);
  }

  downloadCSV(csvDataStr.join("\n"), "Employers");
};

function getFieldTitle(field: string) {
  const titleMap: Record<string, string> = {
    id: "Emp #",
    sCompany: "Organization",
    sCity: "City",
    sState: "State",
    sZip: "Zip",
    sConnector: "Contact Name",
    sEmail: "Contact Email",
    sContactPhone: "Contact Phone",
    iLoanLimit: "Max Loan Amount",
    iApplicationFee: "Application Fee",
  };

  return titleMap[field] || field;
}

function getFieldValueType(field: string) {
  const typeMap: Record<string, string> = {
    id: "contains",
    sCompany: "contains",
    sCity: "contains",
    sState: "contains",
    sZip: "contains",
    sConnector: "contains",
    sEmail: "contains",
    sContactPhone: "contains",
    iLoanLimit: "is",
    iApplicationFee: "is",
  };

  return typeMap[field] || "is";
}

function getFieldValue(field: string, value: any) {
  if (field == "iLoanLimit" || field == "iApplicationFee") {
    const amountMap: Record<string, string> = {
      "1": "<$1000",
      "2": "$1000-$5000",
      "3": "$5000-$10,000",
      "4": "$10,000-$20,000",
      "5": ">$20,000",
    };

    return amountMap[String(value)] || value;
  }

  return value;
}

interface CustomButtonProps {
  onClick: (record: RaRecord) => void;
  source?: string;
  label?: string;
  sortable?: boolean;
}

const EmployersList = () => {
  const refresh = useRefresh();
  const formRef = useRef();
  const [editRecord, setEditRecord] = React.useState<RaRecord | null>(null);
  const [open, setOpen] = React.useState(false);
  const [currentTab, setCurrentTab] = React.useState("info");
  const { successNotify, errorNotify, infoNotify } = useNotify();
  const [record, setRecord] = React.useState<{ total: number } | null>(null);
  const permissions =
    localStorage.getItem("bbaccess")?.toString().split(",") || [];
  if (!permissions.includes("personal_partners")) {
    return <NoPermission />;
  }
  const { data, isLoading, error } = useGetOne("total/partner", { id: 1 });
  React.useEffect(() => {
    setRecord(data);
  }, [data]);

  if (error) {
    errorNotify("Error loading data " + error);
  }
  if (isLoading) {
    return <HmLoading />;
  }
  const total = record ? record.total : 0;

  const CustomActionField = ({ ...props }) => {
    let record = useRecordContext();
    if (!record) return null;

    return (
      <IconButton>
        <EditAttributesOutlined data-record={JSON.stringify(record)} />
      </IconButton>
    );
  };

  const handleRowClick = (
    id: any,
    resource: string,
    record: RaRecord,
    event: React.MouseEvent
  ) => {
    setEditRecord(record);
    setOpen(true);
    event.stopPropagation();
  };
  const handleClose = () => {
    setOpen(false);
    setEditRecord(null);
    refresh();
  };

  const handleRemove = () => {
    var reqUrl = `employers/actions/delete`;
    sendRequest(reqUrl, { id: editRecord?.id }).then((response) => {
      if (response && response.code == "200") {
        successNotify("Data deleted successfully");
        handleClose();
      } else {
        errorNotify("Data not deleted");
      }
    });
    setOpen(false);
    setEditRecord(null);
  };
  const CustomButton = ({ onClick }: CustomButtonProps) => {
    const record = useRecordContext();
    if (!record) return null;

    const handleClick = (showTab: string) => {
      onClick(record);
      setCurrentTab(showTab);
    };
    const handleRemove = () => {
      var reqUrl = `employers/actions/delete`;
      sendRequest(reqUrl, { id: record?.id }).then((response) => {
        if (response && response.code == "200") {
          successNotify("Data deleted successfully");
          handleClose();
        } else {
          errorNotify("Data not deleted");
        }
      });
      setOpen(false);
      setEditRecord(null);
    };
    const handleChangeStatus = () => {
      let newStatus = record.sStatus == 1 ? 0 : 1;

      var reqUrl = "employers/actions/changeStatus";
      var response = sendRequest(reqUrl, {
        id: record.id,
        data: {
          status: newStatus,
        },
      });
      response
        .then((res) => {
          if (res.code == 200) {
            successNotify("Status changed successfully");
            refresh();
          } else {
            errorNotify("Failed to change status! " + res?.message);
          }
        })
        .catch((error) => {
          errorNotify("Error changing status: " + error.message);
        });
    };
    return (
      <>
        <CustomEditIcon onClick={() => handleClick("info")} label="Edit" />
        <CustomEditLinkProductIcon
          onClick={() => handleClick("linkProducts")}
          label="Link products"
        />
        {/* <CustomEditTermIcon
          onClick={() => handleClick("special")}
          label="Set terms"
        /> */}
        <Tooltip
          title={`` + (record?.sStatus == 1 ? "Deactivate" : "Activate") + ``}
          placement="left"
        >
          <IconButton
            size="small"
            onClick={handleChangeStatus}
            sx={{ marginLeft: "5px", p: 1 }}
          >
            {record?.sStatus == 1 ? (
              <SwitchOnIcon color="red" />
            ) : (
              <SwitchIcon color="green" />
            )}
          </IconButton>
        </Tooltip>
        <CustomUniqueURLIcon
          onClick={() => handleClick("uniqueURL")}
          label="Unique URL"
        />
        <CustomEditRemoveIcon onClick={() => handleRemove()} />
      </>
    );
  };

  const handleButtonClick = (record: any) => {
    setOpen(true);
    setEditRecord(record);
  };

  const handleSave = () => {
    const form = document.querySelector(".employer_edit_dialog form");
    if (form) {
      (form as HTMLFormElement).dispatchEvent(
        new Event("submit", { cancelable: true })
      );
    }
  };
  const CustomRowStyle = (record: any) => {
    if (!record) return {};

    if (record?.sStatus == 1 || record?.sStatus == "Yes") {
      return {
        backgroundColor: "#fff",
        "&.MuiTableRow-hover:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      };
    } else {
      return {
        backgroundColor: "#f5f5f5",
        "& td.MuiTableCell-root": {
          color: "gray",
        },
        "& td.MuiTableCell-root span": {
          color: "gray",
        },
        "& td.MuiTableCell-root a": {
          color: "gray",
        },
        "& td.MuiTableCell-root p": {
          color: "gray",
        },
      };
    }
  };
  const authrole = localStorage.getItem("authrole");

  return (
    <>
      <List
        actions={<PartnersListActions selected="employers" />}
        aside={<PartnersListAside totalData={total} selected="employers" />}
        sort={{ field: "created_at", order: "DESC" }}
        filters={getEmployersListFilters()}
        exporter={CustomExporter}
        empty={false}
        emptyWhileLoading={false}
        loading={<HmLoading />}
        sx={{
          width: "100%",
          borderRadius: "0px",
          "& .MuiCard-root": {
            borderRadius: "0px",
          },
          "& .RaList-noResults": {
            marginTop: "65px",
          },
          "& .RaList-actions form": {
            flex: "0 1 350%",
          },
          "& .RaList-actions form .filter-field .MuiInputBase-input": {
            width: "230px",
          },
          "& .RaList-actions .RaFilterForm-filterFormInput:first-child .MuiInputBase-root::before":
            {
              display: "none",
            },
          "& .RaList-actions .filter-field:first-child .MuiFormControl-root": {
            marginLeft: 0,
          },
        }}
        pagination={<PostPagination />}
        perPage={25}
      >
        <Datagrid
          optimized
          rowClick={false}
          empty={false}
          rowSx={CustomRowStyle}
          size="small"
          id="table-employers"
          className="hm-list-datagrid"
          sx={{
            ".MuiTableCell-root": {
              padding: "6px",
            },
            ".RaDatagrid-headerCell:first-child": {
              borderTopLeftRadius: "0px",
            },
            ".RaDatagrid-headerCell:last-child": {
              borderTopRightRadius: "0px",
            },
            "& .column-groups": {
              md: { display: "none" },
              lg: { display: "table-cell" },
            },
            "& .Mui-active": {
              color: "gray",
            },
            "& .MuiTableSortLabel-root": {
              padding: "5px",
            },
            "& .RaDatagrid-headerCell": {
              color: "gray",
              fontSize: "1rem",
              backgroundColor: "#edf4fb",
              wordWrap: "break-word",
            },
            "& .MuiTableCell-paddingCheckbox": {
              display: "none",
            },

            "& .MuiTableCell-root.column-iApplicationFee": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
              textAlign: "right",
            },
            "& .MuiTableCell-root.column-sVerification": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
            },
            "& .MuiTableCell-root.column-sGuaranteeType": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
            },
            "& .MuiTableCell-root.column-iLoanLimit": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdAmountWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdAmountWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdAmountWidth,
              textAlign: "right",
            },
            "& .MuiTableCell-root.column-sConnector": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              textAlign: "left",
              wordBreak: "break-word",
              overflowX: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
            },
            "& .MuiTableCell-root.column-sEmail": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              textAlign: "left",
              wordBreak: "break-word",
              overflowX: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
            },
            "& .MuiTableCell-root.column-sCompany": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
              textAlign: "left",
              wordBreak: "break-word",
              overflowX: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "normal",
            },
            "& .MuiTableCell-root.column-sCity": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
            },
            "& .MuiTableCell-root.column-sState": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdSmallWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdSmallWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdSmallWidth,
            },
            "& .MuiTableCell-root.column-id": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdSmallWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdSmallWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdSmallWidth,
            },
            "& .MuiTableCell-root.column-productNum": {
              minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
              width: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
              maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
            },
            "& .MuiTableCell-root.column-column-action": {
              minWidth: "200px",
              width: "200px",
              maxWidth: "200px",
            },
          }}
        >
          <TextField source="id" label="Emp #" cellClassName="numericText" />
          {/* <TextField source="sCompany" label="Organization" /> */}
          <CustomEmployerNameField source="sCompany" label="Organization" />
          <TextField source="sCity" label="City" />
          <TextField source="sState" label="State" />
          <TextField source="sConnector" label="Contact Name" />
          {/* <TextField source="sEmail" label="Contact Email" /> */}
          {/* <TextField
            source="sContactPhone"
            label="Contact Phone"
            cellClassName="numericText"
          /> */}

          {/* <TextField
                source="sVerification"
                label="Employer Verification"
                sx={{ fontFamily: hmLightTheme?.palette?.numberFontFamily }}
              />
              <TextField
                source="sGuaranteeType"
                label="Guarantee"
                sx={{ fontFamily: hmLightTheme?.palette?.numberFontFamily }}
              /> */}
          {/* <TextField
                source="id"
                label="Advisor"
                sx={{ fontFamily: hmLightTheme?.palette?.numberFontFamily }}
              /> */}

          <CustomProductNameField
            source="productNum"
            label="Products Linked"
            cellClassName="numericText"
            sortable={false}
          />
          {/* <CustomStatusField label="Status" sortable={false} /> */}
          {/* <LoanAmountField
            source="iLoanLimit"
            label="Max Loan Amount"
            cellClassName="numericText"
          /> */}

          {/* <CustomApplicationFeeField
            source="iApplicationFee"
            label="Application Fee"
            cellClassName="numericText"
          /> */}
          {authrole != "admin" && (
            <CustomButton
              source="column-action"
              label="Action"
              onClick={handleButtonClick}
              sortable={false}
            />
          )}
        </Datagrid>
        <CustomLoading />
      </List>
      <Dialog
        className="employer_edit_dialog"
        open={open}
        onClose={handleClose}
        fullWidth={true}
        maxWidth="md"
        PaperProps={{
          component: "form",
          onSubmit: async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            let form = document.querySelector(
              ".employer_edit_dialog_form form"
            );
            let formData = new FormData(form as HTMLFormElement);
            let formJson = Object.fromEntries(formData.entries());

            if (currentTab == "linkProducts") {
              formJson.sStatus = "1";
              let select_products: any[] = [];
              let select = document.querySelectorAll(
                ".employer_edit_dialog .employer_select_product"
              );
              select.forEach((element) => {
                if ((element as HTMLInputElement)?.checked) {
                  select_products.push((element as HTMLInputElement)?.value);
                }
              });
              formJson.productId = select_products.join(",");

              let select_expert = document.querySelectorAll(
                ".employer_edit_dialog .employer_select_expert"
              );
              let select_expert_arr: any[] = [];
              select_expert.forEach((element) => {
                if ((element as HTMLInputElement)?.checked) {
                  select_expert_arr.push((element as HTMLInputElement)?.value);
                }
              });
              formJson.expert = select_expert_arr.join(",");
            } else if (currentTab == "info") {
              if (formJson.sStatus == "1" && formJson.productId != "") {
                formJson.iComplete = "1";
              } else {
                formJson.iComplete = "0";
              }
            }
            console.log("formJson===>", formJson);
            formJson.saveType = currentTab;
            const reqUrl = `employers/actions/edit`;
            const response = await sendRequest(reqUrl, {
              id: editRecord?.id,
              data: formJson,
            });
            if (response && response.code == "200") {
              successNotify("Data saved successfully");
              handleClose();
            } else {
              errorNotify(response?.message || "Data not saved!");
            }
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            borderBottom: "1px solid #e5e5e5",
            color: "#465384",
            textTransform: "uppercase",
          }}
        >
          {/* Employer Edit Record #{editRecord?.id} */}
          {currentTab == "info" && "Edit info"}
          {currentTab == "linkProducts" && "Link Products"}
          {currentTab == "special" && "Set Terms"}
          {currentTab == "uniqueURL" && "Unique URL"}
          {currentTab == "remove" && "Remove"}
          <IconButton
            style={{ position: "absolute", right: "10px", top: "10px" }}
            onClick={handleClose}
          >
            <CloseOutlined />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              width: "100%",
              pt: 0,
              height: "50vh",
              maxHeight: "50vh",
            }}
          >
            {editRecord && (
              <EmployerEdit
                id={editRecord.id}
                record={editRecord}
                basePath="/employers"
                resource="employers"
                curTab={currentTab}
                ref={formRef}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <CustomButtonCancel onClick={handleClose} />
          {currentTab != "uniqueURL" && <CustomButtonSubmit />}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EmployersList;
